/**
 * #1469 — what the Pro Setup Flow tells her when a write didn't land.
 *
 * Lives here, not inline in ProSetupFlowModal, so the wording is testable
 * without mounting a ~1900-line component behind auth, prefs, app-settings and
 * Google-Calendar hooks. The message IS the fix for the second half of #1469,
 * so it needs assertions of its own: a structural scan can only prove the
 * component calls this, not that what it produces names anything.
 *
 * ⚠️ This deliberately reverses half of #665. That issue chose a vague notice
 * ("A couple things didn't save") because naming individual steps would expose
 * internal step names like "zip" or "kid_names" — not a real answer to "what
 * didn't save". That half is still true and still honoured here: the KEYS never
 * reach her, only the labels below. What #665 got wrong is that the vague line
 * was not an answer either. It is the reason the mom who hit the about_me 23505
 * never went back: she was told something was missing and not what.
 *
 * Brand voice: warm, plain, no jargon, no em-dashes.
 *
 * #1478 adds tap-to-retry on top. The Family Profile line stays: it is still
 * the recourse when a retry fails, and it is the ONLY recourse for the two
 * steps that cannot safely be retried (see RETRY_UNSAFE_STEPS below).
 */

/** Internal step key -> the words she would use. */
export const SETUP_FIELD_LABEL: Record<string, string> = {
  zip: "your zip code",
  kid_names: "your crew",
  focus_areas: "what you want help with",
  kid_birthday: "that birthday",
  // #1861 — the Pro setup gender card. Reads "…couldn't save that boy-or-girl answer".
  kid_gender: "that boy-or-girl answer",
  partner: "your partner's name",
  providers: "your people",
  baby_sizes: "your baby's sizes",
  about_me: "about you",
  sms_morning_patch: "your morning texts",
};

/** Her words for a step, or a safe generic if a new step ever lands unmapped. */
export function setupFieldLabel(step: string): string {
  return SETUP_FIELD_LABEL[step] ?? "that last one";
}

/** Sentence-cases a label without touching the rest ("about you" -> "About you"). */
export function capitalizeFirst(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);
}

/** "your crew" / "your crew and about you" / "your crew, about you and more". */
export function joinFieldLabels(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The one-card notice, about the write that just failed.
 *
 * `null` only when a failure arrived with no step recorded, which the current
 * call sites make impossible; it keeps the old wording rather than rendering a
 * sentence with a hole in it.
 */
export function inlineFailureMessage(step: string | null, unverified = false): string {
  // #1857 — the write's outcome could not be read. "Didn't save" would be a
  // claim the helper could not support; the retry button under this line is
  // the thing that settles it, and the upsert is idempotent so tapping it is
  // safe whether or not the first attempt landed.
  if (unverified) {
    const what = step ? setupFieldLabel(step) : "that last one";
    return `Couldn't confirm ${what} saved. Tap to retry, or add it in Family Profile anytime.`;
  }
  if (!step) return "That last one didn't save. You can add it in Family Profile anytime.";
  return `${capitalizeFirst(setupFieldLabel(step))} didn't save. You can add it in Family Profile anytime.`;
}

/** One failed step as the summary sees it. `unverified` is the #1857 third state. */
export interface FailedStepSummary {
  step: string;
  unverified?: boolean;
}

/**
 * The Close-card summary, naming every field that failed this session.
 *
 * Accepts bare step keys (the pre-#1857 shape, still how the tests and the
 * e2e spec call it) or `{ step, unverified }` entries. Unverified steps get
 * their own sentence after the failed ones, so a mixed session reads as two
 * true statements rather than one that is half false.
 */
export function summaryFailureMessage(steps: Array<string | FailedStepSummary>): string {
  const entries = steps.map((s) => (typeof s === "string" ? { step: s } : s));
  const failed = entries.filter((e) => !e.unverified).map((e) => e.step);
  const unverified = entries.filter((e) => e.unverified).map((e) => e.step);
  if (entries.length === 0) return "A couple things didn't save. You can add them in Family Profile.";
  const parts: string[] = [];
  if (failed.length > 0) {
    const names = capitalizeFirst(joinFieldLabels(failed.map(setupFieldLabel)));
    parts.push(`${names} didn't save. You can add ${failed.length === 1 ? "it" : "them"} in Family Profile.`);
  }
  if (unverified.length > 0) {
    const names = joinFieldLabels(unverified.map(setupFieldLabel));
    parts.push(`Couldn't confirm ${names} saved. Tap to retry${failed.length > 0 ? "" : ", or add it in Family Profile"}.`);
  }
  return parts.join(" ");
}

// ── #1478: tap to retry ─────────────────────────────────────────────────────

/** The button. Short, an instruction, and it says what the tap does. */
export const RETRY_LABEL = "Tap to retry";

/** While the retry is in flight. Present tense, so the button reads as busy. */
export const RETRYING_LABEL = "Saving...";

/**
 * Steps whose write CANNOT be safely re-run, so no retry is offered.
 *
 * **Empty as of #1528 — deliberately kept, not deleted.**
 *
 * #1478 assumed every Pro Setup write was either an `upsert_family_fact` RPC or
 * an insert a sibling issue would convert, and therefore idempotent. That held
 * for seven of the nine. Two were neither, and both kept the named failure
 * message and lost the button:
 *
 *   providers  -> createAppointmentReminders() APPENDED
 *                 `[...existing.config.items, ...newItems]`, so a retry gave
 *                 her the same dentist twice in one tracker.
 *   baby_sizes -> createSizeCheckTask() was a bare insert against a type whose
 *                 invariant is one row per kid, so a retry made a second one
 *                 that TasksTab explicitly refuses to merge.
 *
 * #1528 made both idempotent — the first merges on (category, provider name),
 * the second finds this kid's active row and updates it in place — so both
 * cards now offer a retry and this list is empty.
 *
 * ⚠️ The list stays because the RULE stays: a retry that silently duplicates a
 * row is worse than the named message it replaces. A future step whose write is
 * not safe to re-run belongs here, and `canRetryStep` is the one place that
 * decision is read. Emptying the list is not the same as deciding the question
 * cannot arise again.
 *
 * ⚠️ Idempotence of the WRITE is necessary but not sufficient — the retry
 * CLOSURE has to be safe too. The providers card writes to `family_providers`
 * (a bare insert with no uniqueness behind it) before it touches reminders, so
 * its closure re-runs that insert only when it did not already land. See
 * handleProvidersNext in ProSetupFlowModal.
 */
export const RETRY_UNSAFE_STEPS: readonly string[] = [];

/** After a retry that landed. Says which one, so the loop actually closes. */
export function retrySucceededMessage(step: string | null): string {
  if (!step) return "Saved 💜";
  return `${capitalizeFirst(setupFieldLabel(step))} saved 💜`;
}

/**
 * After a retry that failed again.
 *
 * ⚠️ Not the same sentence as the first failure. Repeating it verbatim would
 * read as if the tap did nothing at all, and a retry with no visible outcome is
 * the exact silent-success trap this issue exists to close.
 */
export function retryFailedMessage(step: string | null, unverified = false): string {
  // #1857 — a retry that ALSO could not be read. Still not "isn't saving":
  // two lost responses in a row are two unknowns, not a failure.
  if (unverified) {
    const what = step ? setupFieldLabel(step) : "that one";
    return `Still couldn't confirm ${what} saved. You can check Family Profile in a bit.`;
  }
  const what = step ? capitalizeFirst(setupFieldLabel(step)) : "That one";
  return `${what} still isn't saving. You can add it in Family Profile anytime.`;
}

/** Whether this step gets a retry affordance at all. */
export function canRetryStep(step: string | null): boolean {
  return !!step && !RETRY_UNSAFE_STEPS.includes(step);
}
